import { GoogleMap, Marker, useLoadScript } from '@react-google-maps/api';
import { Skeleton } from 'baseui/skeleton';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { MapPin, X } from 'react-feather';

import { Button } from 'components/Button';
import { useClientQuery } from 'services/api/clients/endpoints';
import { useTypedSelector } from 'store/store';
import { useLocale } from 'utils/hooks/useLocale';

const MAPS_KEY = process.env.REACT_APP_MAPS_KEY;

const containerStyle = {
  width: '100%',
  height: '500px',
};

const defaultCenter = {
  lat: 39.8283, // Center of US
  lng: -98.5795,
};

const isLatLngInRange = (lat: number, lng: number) =>
  lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;

const toValidLatLng = (
  latValue: unknown,
  lngValue: unknown,
): google.maps.LatLngLiteral | undefined => {
  const lat = Number(latValue);
  const lng = Number(lngValue);

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return undefined;
  if (lat === 0 && lng === 0) return undefined;
  if (!isLatLngInRange(lat, lng)) return undefined;

  return { lat, lng };
};

export interface MapPickerLocation {
  lat: number;
  lng: number;
  address: string;
  street: string;
  street2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  timeZone: string;
}

interface MapPickerProps {
  onSelect: (location: MapPickerLocation) => void;
  onClose: () => void;
  initialLocation?: { lat: number; lng: number };
}

export function MapPicker({ onSelect, onClose, initialLocation }: MapPickerProps) {
  const { t } = useLocale();
  const clientId = useTypedSelector((s) => s.auth.userCredentials.clientId);
  const clientQuery = useClientQuery(clientId, { skip: clientId === 0 });
  const [address, setAddress] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [geocodedClientCenter, setGeocodedClientCenter] = useState<
    google.maps.LatLngLiteral | undefined
  >(undefined);

  const clientAddressLocation = useMemo(() => {
    const lat = clientQuery.data?.address?.lat;
    const lng = clientQuery.data?.address?.lng;
    return toValidLatLng(lat, lng);
  }, [clientQuery.data?.address?.lat, clientQuery.data?.address?.lng]);

  const clientAddressString = useMemo(() => {
    const address = clientQuery.data?.address;
    if (!address) return '';
    return [
      address.street,
      address.street2,
      address.city,
      address.state,
      address.postalCode,
      address.country,
    ]
      .map((part) => (part ?? '').toString().trim())
      .filter(Boolean)
      .join(', ');
  }, [clientQuery.data?.address]);

  const initialLocationCenter = useMemo(() => {
    return toValidLatLng(initialLocation?.lat, initialLocation?.lng);
  }, [initialLocation?.lat, initialLocation?.lng]);

  const [selectedLocation, setSelectedLocation] = useState<
    google.maps.LatLngLiteral | null
  >(initialLocationCenter ?? null);

  const mapCenter = useMemo(() => {
    return (
      initialLocationCenter ||
      clientAddressLocation ||
      geocodedClientCenter ||
      defaultCenter
    );
  }, [initialLocationCenter, clientAddressLocation, geocodedClientCenter]);
  const mapZoom = initialLocationCenter ? 15 : mapCenter === defaultCenter ? 4 : 9;
  const effectiveSelectedLocation = selectedLocation || initialLocationCenter || null;

  const { isLoaded, loadError } = useLoadScript({
    id: 'google-map-script',
    googleMapsApiKey: MAPS_KEY || '',
  });

  useEffect(() => {
    setSelectedLocation(initialLocationCenter ?? null);
  }, [initialLocationCenter]);

  useEffect(() => {
    if (!isLoaded) return;
    if (initialLocationCenter || clientAddressLocation) {
      setGeocodedClientCenter(undefined);
      return;
    }
    if (!clientAddressString) return;

    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ address: clientAddressString }, (results, status) => {
      if (status !== 'OK' || !results || !results[0]?.geometry?.location) return;
      const location = results[0].geometry.location;
      setGeocodedClientCenter({ lat: location.lat(), lng: location.lng() });
    });
  }, [
    isLoaded,
    initialLocationCenter,
    clientAddressLocation,
    clientAddressString,
  ]);

  const handleMapClick = useCallback(
    (e: google.maps.MapMouseEvent) => {
      if (e.latLng) {
        const clickedLocation = { lat: e.latLng.lat(), lng: e.latLng.lng() };
        setSelectedLocation(clickedLocation);
        setIsLoading(true);

        // Reverse geocode to get address
        const geocoder = new google.maps.Geocoder();
        geocoder.geocode({ location: clickedLocation }, (results, status) => {
          setIsLoading(false);
          if (status === 'OK' && results && results[0]) {
            const result = results[0];
            setAddress(result.formatted_address);
          } else {
            setAddress('Address not found');
          }
        });
      }
    },
    []
  );

  const handleConfirm = useCallback(() => {
    if (!effectiveSelectedLocation) return;

    setIsLoading(true);
    const geocoder = new google.maps.Geocoder();

    geocoder.geocode({ location: effectiveSelectedLocation }, (results, status) => {
      setIsLoading(false);

      if (status === 'OK' && results && results[0]) {
        const result = results[0];
        const components = result.address_components;

        // Extract address components
        const getComponent = (type: string, useShortName = false) => {
          const component = components.find((c) => c.types.includes(type));
          return component ? (useShortName ? component.short_name : component.long_name) : '';
        };

        const streetNumber = getComponent('street_number');
        const route = getComponent('route');
        const street = streetNumber && route ? `${streetNumber} ${route}` : route || '';

        // Get timezone using TimeZone API
        const timestamp = Math.floor(Date.now() / 1000);
        const timeZoneUrl = `https://maps.googleapis.com/maps/api/timezone/json?location=${effectiveSelectedLocation.lat},${effectiveSelectedLocation.lng}&timestamp=${timestamp}&key=${MAPS_KEY}`;

        fetch(timeZoneUrl)
          .then(res => res.json())
          .then(tzData => {
            const location: MapPickerLocation = {
              lat: effectiveSelectedLocation.lat,
              lng: effectiveSelectedLocation.lng,
              address: result.formatted_address,
              street: street,
              street2: '',
              city: getComponent('locality') || getComponent('sublocality') || '',
              state: getComponent('administrative_area_level_1', true),
              postalCode: getComponent('postal_code'),
              country: getComponent('country', true),
              timeZone: tzData.timeZoneId || '',
            };

            onSelect(location);
          })
          .catch(() => {
            // If timezone fetch fails, still return location without timezone
            const location: MapPickerLocation = {
              lat: effectiveSelectedLocation.lat,
              lng: effectiveSelectedLocation.lng,
              address: result.formatted_address,
              street: street,
              street2: '',
              city: getComponent('locality') || getComponent('sublocality') || '',
              state: getComponent('administrative_area_level_1', true),
              postalCode: getComponent('postal_code'),
              country: getComponent('country', true),
              timeZone: '',
            };

            onSelect(location);
          });
      }
    });
  }, [effectiveSelectedLocation, onSelect]);

  if (loadError) {
    return (
      <div className="p-4 text-red-600">
        {t('Map cannot be loaded right now, sorry.')}
      </div>
    );
  }

  if (!isLoaded) {
    return <Skeleton height="500px" />;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="relative w-full max-w-4xl rounded-lg bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold">{t('Select Location on Map')}</h2>
          <button
            onClick={onClose}
            className="rounded-full p-1 hover:bg-gray-100"
            type="button"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="mb-4">
          <p className="text-sm text-gray-600">
            {t('Click on the map to drop a pin and select a location')}
          </p>
        </div>

        <div className="mb-4 overflow-hidden rounded-lg border border-gray-300">
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={mapCenter}
            zoom={mapZoom}
            onClick={handleMapClick}
            options={{
              mapTypeControl: true,
              streetViewControl: false,
            }}
          >
            {effectiveSelectedLocation && (
              <Marker
                position={effectiveSelectedLocation}
                title={t('Selected Location')}
              />
            )}
          </GoogleMap>
        </div>

        {effectiveSelectedLocation && (
          <div className="mb-4 rounded-lg bg-gray-50 p-3">
            <div className="flex items-start space-x-2">
              <MapPin className="mt-1 h-5 w-5 flex-shrink-0 text-gray-500" />
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-700">
                  {t('Selected Location')}:
                </p>
                <p className="text-sm text-gray-600">
                  {isLoading ? t('Loading address...') : address}
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  Lat: {effectiveSelectedLocation.lat.toFixed(6)}, Lng:{' '}
                  {effectiveSelectedLocation.lng.toFixed(6)}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end space-x-3">
          <Button
            type="button"
            kind="secondary"
            onClick={onClose}
          >
            {t('Cancel')}
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={!effectiveSelectedLocation || isLoading}
            isLoading={isLoading}
          >
            {t('Confirm Location')}
          </Button>
        </div>
      </div>
    </div>
  );
}
